import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import React from 'react'
import { scrollaInIVy } from '../tangentbord'
import { MalindexConfig, MalindexMal } from '../../../api/malindex/malindexApi'

/**
 * MalRedigerare – formulering av mål.
 *
 * Målen är FRISTÅENDE och kopplas inte till något område (Antons beslut). Antalet
 * begränsas av config.maxMal.
 *
 * Varje mål har två fält: brukarens egna ord ordagrant, och själva målet formulerat
 * enligt mallen. De egna orden står först eftersom det är dem man läser upp vid
 * uppföljningen – men det är MÅLET som är obligatoriskt. Det är den texten som bär
 * målet i översikten och i uppföljningen, och den får inte saknas.
 */

export interface MalRedigerareProps {
  config: MalindexConfig
  mal: MalindexMal[]
  onAndra: (mal: MalindexMal[]) => void
}

/** Ett mål är färdigt när målformuleringen finns. De egna orden är frivilliga. */
export const arIfyllt = (m: MalindexMal) => m.maltext.trim().length > 0

/** Någon har börjat skriva i målet – i något av fälten. */
const arPaborjad = (m: MalindexMal) => m.egnaOrd.trim().length > 0 || m.maltext.trim().length > 0

/**
 * Mål som är påbörjade men saknar målformulering.
 *
 * De blockerar sparandet i stället för att kastas tyst. En helt tom rad är däremot
 * någon som lagt till ett mål och ångrat sig, och den försvinner utan att säga till.
 */
export const ofullstandigaMal = (mal: MalindexMal[]) => mal.filter((m) => arPaborjad(m) && !arIfyllt(m))

const nyttMalId = () => `m${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`

const MalRedigerare: React.FC<MalRedigerareProps> = ({ config, mal, onAndra }) => {
  const kanLaggaTill = mal.length < config.maxMal

  const laggTill = () => {
    if (!kanLaggaTill) return
    onAndra([...mal, { malId: nyttMalId(), egnaOrd: '', maltext: '' }])
  }

  const uppdatera = (malId: string, delta: Partial<MalindexMal>) =>
    onAndra(mal.map((m) => (m.malId === malId ? { ...m, ...delta } : m)))

  const taBort = (malId: string) => onAndra(mal.filter((m) => m.malId !== malId))

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="m-0 text-base font-semibold text-gray-800">Formulera mål</p>
        <p className="m-0 text-sm text-gray-500">Formulera max {config.maxMal} mål.</p>
      </div>

      {/* Knappen först: man lägger till ett mål och skriver det sedan. Låg den sist
          fick man scrolla förbi målen för att skapa nästa. Mallen finns kvar som
          platshållare i fältet, där den behövs. */}
      <div>
        <button
          type="button"
          disabled={!kanLaggaTill}
          title={kanLaggaTill ? '' : `Högst ${config.maxMal} mål per besök`}
          onClick={laggTill}
          className="rounded-sm border border-pejlen-500 px-4 py-2 text-base text-pejlen-500 transition-colors hover:bg-pejlen-50 disabled:cursor-not-allowed disabled:border-gray-300 disabled:text-gray-400"
        >
          <PlusOutlined /> Lägg till mål
        </button>
      </div>

      {mal.length === 0 && (
        <p className="m-0 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-3 text-base text-gray-500">
          Inga mål än. Lägg till ett när ni formulerat det tillsammans.
        </p>
      )}

      {mal.map((m, i) => (
        <div key={m.malId} className="rounded-lg border border-gray-100 bg-gray-50 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-base font-semibold text-gray-800">Mål {i + 1}</span>
            <Tooltip title="Ta bort målet">
              <button
                type="button"
                aria-label={`Ta bort mål ${i + 1}`}
                onClick={() => taBort(m.malId)}
                className="rounded-sm p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          </div>

          <label className="mb-1 block text-sm font-medium text-gray-700">
            {config.mal.egnaOrdEtikett} <span className="font-normal text-gray-400">(valfritt)</span>
          </label>
          <TextArea
            value={m.egnaOrd}
            onChange={(e) => uppdatera(m.malId, { egnaOrd: e.target.value })}
            placeholder='t.ex. "Jag vill kunna mata änderna igen."'
            autoSize={{ minRows: 2, maxRows: 4 }}
            onFocus={scrollaInIVy}
            style={{ resize: 'none', fontSize: 16 }}
          />

          <label className="mb-1 mt-3 block text-sm font-medium text-gray-700">Mål</label>
          <TextArea
            value={m.maltext}
            onChange={(e) => uppdatera(m.malId, { maltext: e.target.value })}
            placeholder={config.mal.mallText}
            autoSize={{ minRows: 2, maxRows: 5 }}
            onFocus={scrollaInIVy}
            style={{ resize: 'none', fontSize: 16 }}
          />
          {/* Visas först när någon börjat skriva. Att skälla på en tom, nyss tillagd
              rad vore att kritisera innan något hänt. */}
          {arPaborjad(m) && !arIfyllt(m) && (
            <p className="mb-0 mt-1 text-sm text-red-500">Målet måste formuleras för att besöket ska kunna sparas.</p>
          )}
        </div>
      ))}
    </div>
  )
}

export default MalRedigerare
